import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Coins, Compass, Gem } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { buildCity, tileOf } from "@/city3d/layout";
import { City3D } from "@/city3d/City3D";
import { Newspaper } from "@/city3d/Newspaper";
import { PeoplePicker } from "@/city3d/PeoplePicker";
import { Tour } from "@/city3d/Tour";
import { Chat } from "@/city3d/Chat";
import { Toaster } from "@/components/ui/sonner";
import { sfx } from "@/audio";
import { Face, Minimap, TickerBar } from "@/city3d/Hud";
import { ActionDock } from "@/hud/ActionDock";
import { CoinLayer, flyCoins } from "@/hud/CoinFly";
import { Drawer } from "@/hud/Drawer";
import { GoalTracker } from "@/hud/GoalTracker";
import { Guide, useGuide } from "@/hud/Guide";
import { Inspector } from "@/hud/Inspector";
import { PANEL } from "@/hud/kit";
import { card, shipReadyCard, useNotices, usePresence } from "@/hud/Notices";
import { RevealLayer, reveal } from "@/hud/Reveal";
import { Splash } from "@/hud/Splash";
import { OfflineBanner, SignInCta } from "@/hud/Status";
import { TopBar } from "@/hud/TopBar";
import { ladder } from "@/hud/Leaderboard";
import { useEconomy } from "@/hud/useEconomy";
import { fmt, shareDock, stopOf, today, useHotkeys, useMedia, useOnline } from "@/hud/util";

const TITLE = "XField: X finally has a city";
const DAY = 86400000;
const SNAP_VERSION = "close-up-4";
const FILM = new URLSearchParams(window.location.search).has("film");
const PLACE = new URLSearchParams(window.location.search).get("place");
const SIGNIN_ERRORS = {
  off: "Sign in with X is switched off right now.",
  cancelled: "Sign in was cancelled on X.",
  expired: "That sign in took too long or opened in another browser. Try again.",
  x_error: "X didn't let us in. Try again in a minute.",
};

// Pictures your house and street from the city, for your share card. Only when something on it changed, or a day on.
function useSnapshot({ me, mine, homeOf, snapRef, onSaved }) {
  const key = me?.status === "finished" && mine
    ? [SNAP_VERSION, mine.x, mine.z, mine.level, mine.stack.length, mine.owner.m, mine.owner.f, ...me.neighbours.map((p) => p.k + p.s)].join("|") : null;
  useEffect(() => {
    if (!key) return;
    const store = `xf_snap_${me.k}`;
    const prev = JSON.parse(localStorage.getItem(store) || "null");
    if (me.has_snap && prev?.key === key && Date.now() - prev.at < DAY) return;
    const id = setTimeout(async () => {
      if (!snapRef.current) return;
      const dark = me.neighbours.filter((p) => p.s === "r" && homeOf[p.k]).map((p) => homeOf[p.k]);
      const points = [{ k: me.k, pos: [mine.x, mine.top + 0.1, mine.z] }, ...dark.map((h) => ({ k: h.owner.k, pos: [h.x, h.top + 0.05, h.z] }))];
      const { blob, points: at } = await snapRef.current({ at: [mine.x, mine.z, mine.y], points });
      const inFrame = (p) => p.x > 30 && p.x < 610 && p.y > 90 && p.y < 610;
      const form = new FormData();
      form.append("image", blob, "house.jpg");
      form.append("meta", JSON.stringify({ me: [at[0].x, at[0].y], faces: at.slice(1).filter(inFrame).map(({ k, x, y }) => ({ k, x, y })) }));
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/me/snapshot`, { method: "POST", body: form, credentials: "include" }).catch(() => null);
      if (res?.ok) {
        localStorage.setItem(store, JSON.stringify({ key, at: Date.now() }));
        onSaved();
      }
    }, 3000);
    return () => clearTimeout(id);
  }, [key, me?.has_snap]); // eslint-disable-line react-hooks/exhaustive-deps
}

const dropPlaceParam = () => {
  const url = new URL(window.location.href);
  if (!url.searchParams.has("place")) return;
  url.searchParams.delete("place");
  window.history.replaceState(null, "", url.pathname + url.search);
};

export default function CityPage() {
  const [town, setTown] = useState(null);
  const [me, setMe] = useState(null);
  const [hover, setHover] = useState(null);
  const [picked, setPicked] = useState(null);
  const [drawer, setDrawer] = useState(() => (!PLACE || PLACE === "ledger" ? null : PLACE === "hall" ? { tab: "hall" } : { tab: "place", id: PLACE }));
  const [paper, setPaper] = useState(PLACE === "ledger");
  const [chat, setChat] = useState(false);
  const [unread, setUnread] = useState(0);
  const [collected, setCollected] = useState(false);
  const [nearby, setNearby] = useState(null);
  const [focus, setFocus] = useState(null);
  const [flashes, setFlashes] = useState([]);
  const [skipped, setSkipped] = useState(false);
  const [replay, setReplay] = useState(() => new URLSearchParams(window.location.search).has("tour"));
  const [picking, setPicking] = useState(false);
  const [signinError, setSigninError] = useState(null);
  const auth = useAuth();
  const online = useOnline();
  const live = usePresence();
  const phone = useMedia("(max-width: 639px)");
  const [guide, setGuide] = useGuide();
  const labelNodes = useRef([]);
  const snapRef = useRef(null);
  const flashId = useRef(0);
  const lastLots = useRef(null);
  const pending = useRef([]);
  const seen = useRef(null);
  const startedRef = useRef(false);
  const [built, setBuilt] = useState(false);
  const onBuilt = useCallback(() => setBuilt(true), []);
  const atHandle = new URLSearchParams(window.location.search).get("at")?.toLowerCase();
  const lit = Boolean(auth.user) && me?.status === "finished";
  const eco = useEconomy(lit, (berths) => shipReadyCard(berths, collect));
  const { econ } = eco;

  const flash = (list) => {
    const at = performance.now();
    setFlashes((fs) => [...fs.filter((f) => at - f.at < 1500), ...list.slice(0, 64).map((f) => ({ ...f, at, id: flashId.current++ }))]);
  };
  const version = useRef(null);
  const loadTown = useCallback(() => api(`/town${version.current === null ? "" : `?v=${version.current}`}`).then((t) => {
    if (t.unchanged) return;
    version.current = t.version;
    const was = lastLots.current, sig = (r) => `${r.s}${r.f}${r.u}${r.lv ?? ""}`;
    lastLots.current = new Map(t.residents.map((r) => [r.k, sig(r)]));
    if (was) pending.current = t.residents.filter((r) => (was.has(r.k) ? was.get(r.k) !== sig(r) : r.lv != null))
      .map((r) => ({ k: r.k, rise: !was.has(r.k) }));
    setTown(t);
  }).catch(() => {}), []);
  const loadMe = useCallback(() => {
    if (!auth.user) return setMe(null);
    return api(`/residents/${auth.user.h}`).then((m) => {
      setMe(m);
      const news = m.news || [];
      const last = seen.current ?? localStorage.getItem("xf_seen") ?? "";
      const fresh = news.filter((n) => n.at > last);
      if (seen.current !== null && fresh.length) setNearby(fresh[0]);
      seen.current = news[0]?.at > last ? news[0].at : last;
      localStorage.setItem("xf_seen", seen.current);
    }).catch(() => setMe(null));
  }, [auth.user]);
  useEffect(() => {
    loadTown();
    if (FILM) return;
    const id = setInterval(loadTown, 5000);
    return () => clearInterval(id);
  }, [loadTown]);
  useEffect(() => { loadMe(); }, [loadMe, town?.version]);
  useEffect(() => { document.title = TITLE; }, []);
  useEffect(() => { setCollected(localStorage.getItem("xf_collected") === `${auth.user?.k}:${today()}`); }, [auth.user]);
  useEffect(() => {
    const url = new URL(window.location.href);
    const why = url.searchParams.get("signin");
    if (!why) return;
    setSigninError(SIGNIN_ERRORS[why] || SIGNIN_ERRORS.x_error);
    url.searchParams.delete("signin");
    window.history.replaceState(null, "", url.pathname + url.search);
  }, []);
  const issue = town?.paper?.issue;
  const newPaper = issue > Number(localStorage.getItem("xf_paper") || 0);

  const city = useMemo(() => {
    if (!town) return null;
    const c = buildCity(town);
    const mine = c.homes.find((h) => h.owner.k === auth.user?.k);
    c.mine = mine ? [mine.x, mine.z] : null;
    return c;
  }, [town, auth.user]);
  const homeOf = useMemo(() => Object.fromEntries((city?.homes || []).map((h) => [h.owner.k, h])), [city]);
  const myHome = homeOf[auth.user?.k];
  const weekRank = useMemo(() => ladder(town?.residents || [], "wk").find((r) => r.k === auth.user?.k)?.rank, [town, auth.user]);
  useEffect(() => {
    const list = pending.current.filter((p) => homeOf[p.k]).map((p) => {
      const h = homeOf[p.k];
      return { x: h.x, z: h.z, y: h.y, rise: p.rise, color: p.rise && h.owner.s === "r" ? "#94A3B8" : "#FCD34D" };
    });
    pending.current = [];
    if (list.length) flash(list);
  }, [homeOf]);
  useSnapshot({ me, mine: myHome, homeOf, snapRef, onSaved: loadMe });
  useEffect(() => {
    if (!city || startedRef.current) return;
    startedRef.current = true;
    const there = atHandle && city.homes.find((h) => h.owner.k === atHandle);
    setFocus(there ? [there.x, there.z] : city.mine || [1.5, 1.5]);
    if (there) setPicked(there);
  }, [city, atHandle]);

  const closeDrawer = () => { dropPlaceParam(); setDrawer(null); };
  const show = (tab, section, extra) => { setChat(false); setDrawer({ tab, section, ...extra, t: Date.now() }); };
  const toggle = (tab, section) => (drawer?.tab === tab && (!section || drawer.section === section) ? closeDrawer() : show(tab, section));
  const toggleChat = () => { setDrawer(null); setChat((c) => !c); };
  const openPlace = (id) => {
    setPicked(null);
    if (id === "ledger") setPaper(true);
    else show(id === "hall" ? "hall" : "place", undefined, { id });
  };
  const pick = (it) => {
    setFocus([it.x, it.z]);
    if (it.owner) {
      setPicked(it);
      setDrawer((d) => (d?.tab === "place" || window.innerWidth < 640 ? null : d));
    } else if (it.mark?.kind === "place") openPlace(it.mark.id);
    else if (it.mark?.kind === "city_hall") openPlace("hall");
    else {
      setPicked(null);
      setDrawer((d) => (d?.tab === "place" ? null : d));
    }
  };
  const fly = (p) => homeOf[p.k] && pick(homeOf[p.k]);
  const flyHome = () => city.mine && setFocus([...city.mine]);
  const signIn = () => auth.login(window.location.pathname + window.location.search);

  const collect = async (from) => {
    const r = await eco.act("/dock/collect");
    if (!r) return;
    localStorage.setItem("xf_collected", `${auth.user.k}:${today()}`);
    setCollected(true);
    sfx("collect");
    // Lucky cargo gets the slot-reel reveal (which showers the coins itself); a big haul gets the BIG WIN banner after it.
    const lucky = r.items.filter((i) => i.luck).sort((a, b) => b.mult - a.mult)[0];
    if (lucky) reveal({ kind: "luck", item: lucky });
    else flyCoins(from?.x == null ? undefined : from, Math.min(40, Math.max(14, Math.round(r.got / 20))));
    if (r.got >= 1000 && lucky?.luck !== "jackpot") reveal({ kind: "win", n: r.got, mega: r.got >= 5000 });
    setTimeout(() => card({ icon: Coins, gold: true, title: r.upkeep ? `Collected, after ${fmt(r.upkeep)} upkeep` : "Collected", amount: `+${fmt(r.got)}` },
      { id: "collected", duration: 4500, sound: false }), lucky ? 5800 : 0);
  };
  // A trade opens a mystery crate.
  const trade = async (p) => {
    const r = await eco.act(`/visit/${p.h}`);
    if (!r) return;
    reveal({ kind: "crate", prize: r.crate, top: r.top, host: r.host ?? p, boost: r.boost });
  };
  const invite = () => (me.invites_left > 0 ? setPicking(true) : shareDock(me));

  useNotices(econ, { fly, celebrate: () => { flyCoins(undefined, 20); sfx("win"); }, home: flyHome, hall: () => show("hall"), duels: (p) => show("economy", "duels", p && { challenge: p }),
    rent: () => show("economy", "uptown"), dock: () => show("economy", "dock") });
  useEffect(() => {
    if (nearby) card({ face: nearby.p, tone: "green", title: `@${nearby.p.h} moved in near you`, actions: [["Trade", () => fly(nearby.p)]] });
  }, [nearby]); // eslint-disable-line react-hooks/exhaustive-deps
  useHotkeys({
    s: () => toggle("shop"), e: () => toggle("economy"), l: () => toggle("leaders", "week"), d: () => toggle("economy", "duels"), h: () => toggle("hall"), c: toggleChat,
    Escape: () => { closeDrawer(); setChat(false); setPicked(null); },
  });

  if (!city) return <Splash />;
  if (FILM) {
    return (
      <div className="fixed inset-0">
        <City3D city={city} labels={[]} labelNodes={labelNodes} flashes={[]} onHover={() => {}} onPick={() => {}} newest={town.newest} onFace={() => {}} film />
      </div>
    );
  }
  const nbById = Object.fromEntries(town.neighbourhoods.map((n) => [n.id, n]));
  const h = hover?.item;
  const touring = lit && myHome && econ && (replay || (!me.done?.tour && !skipped));
  const labels = [...city.labels,
    ...(myHome ? [{ kind: "me", key: "me", pos: [myHome.x, myHome.top + 0.5, myHome.z], avatar: auth.user.avatar_url, score: econ?.worth ?? myHome.owner.e }] : []),
    ...(econ?.offer ? [{ kind: "lot", key: "lot", pos: [tileOf(econ.offer.lot[0]), 0.35, tileOf(econ.offer.lot[1])], price: econ.offer.price }] : [])];
  const refresh = () => Promise.all([loadTown(), loadMe()]);
  const closePaper = () => {
    if (issue) localStorage.setItem("xf_paper", String(issue));
    dropPlaceParam();
    setPaper(false);
  };
  const markDone = (step) => api("/me/done", { method: "POST", body: { step } }).then(loadMe).catch(() => {});
  const endTour = (next) => {
    setReplay(false);
    setSkipped(true);
    flyHome();
    markDone("tour");
    if (next === "invite") setPicking(true);
    else if (next === "ship") show("economy", "dock");
  };
  const stops = touring ? [[myHome.x, myHome.z, 84]] : [];
  const pickedNow = picked && homeOf[picked.owner.k];
  const at = atHandle && homeOf[atHandle]?.owner;
  const calm = !touring && !picking;
  const mayorK = econ?.hall.mayor?.k ?? town.hall?.mayor?.k;

  return (
    <div className="fixed inset-0 overflow-clip select-none">
      <h1 className="sr-only">XField</h1>
      <City3D city={city} labels={labels} labelNodes={labelNodes} focus={focus} flashes={flashes} snapRef={lit ? snapRef : null}
        onHover={setHover} onPick={pick} newest={town.newest} onFace={(k) => fly({ k })} meKey={auth.user?.k} onCollect={collect} onReady={onBuilt} />
      <div className="absolute inset-0 pointer-events-none">
        {labels.map((l, i) => (
          <div key={l.key || i} ref={(el) => { labelNodes.current[i] = el; }} className="absolute left-0 top-0 will-change-transform">
            {l.kind === "me" ? (
              <div className="flex flex-col items-center" data-testid="my-house-marker">
                <img src={l.avatar} alt="" className="h-10 w-10 rounded-full border-[3px] border-white bg-sky-light" />
                <span className="acc-fill mt-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums">You{l.score != null && ` · ${fmt(l.score)}`}</span>
              </div>
            ) : l.kind === "lot" ? (
              <div className="flex flex-col items-center" data-testid="my-lot-marker">
                <button type="button" onClick={() => show("economy", "uptown")} className="hud-panel hud-label lift pointer-events-auto whitespace-nowrap rounded-full border-dashed !border-x-accent px-2.5 py-0.5 text-[11px] font-semibold tabular-nums">
                  For rent · {fmt(l.price)}/wk
                </button>
                <span className="h-3 w-0.5 bg-x-accent" />
              </div>
            ) : l.kind === "place" ? (
              <button type="button" onClick={() => openPlace(l.id)} data-testid={`place-label-${l.id}`}
                className="hud-panel hud-label lift pointer-events-auto whitespace-nowrap rounded-full px-3 py-1 text-sm font-semibold">
                {l.text}
              </button>
            ) : (
              <div className="flex flex-col items-center">
                <span className="hud-panel hud-label flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-sm font-semibold">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: l.color }} />{l.text}
                </span>
                <span className="mt-1 flex items-center -space-x-1.5" title={l.host ? `@${l.host}'s mutuals` : undefined}>
                  {l.faces.map((p, i) => (
                    <button key={p.k} type="button" onClick={() => fly(p)} className="pointer-events-auto hover:z-10 hover:scale-110 transition-transform">
                      <Face p={p} size={i ? 22 : 28} ring={i ? "var(--x-surface)" : l.color} />
                    </button>
                  ))}
                  {l.size > l.faces.length && (
                    <span className="hud-panel hud-label !ml-1 rounded-full px-1.5 text-[11px] font-semibold tabular-nums">+{l.size - l.faces.length}</span>
                  )}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      <TopBar user={auth.user} me={me} econ={econ} rank={weekRank} clockOffset={eco.clockOffset} stop={econ ? stopOf(econ, me) : 0} unread={unread} newPaper={newPaper} panel={Boolean(drawer || chat)}
        onFly={flyHome} onOpen={toggle} onChat={toggleChat} onLedger={() => setPaper(true)}
        guide={guide} onGuide={setGuide} onTour={() => setReplay(true)} onSignIn={signIn} onSignOut={auth.logout} />
      {(!online || eco.error) && <OfflineBanner />}
      {me && !lit && (
        <div className={`${PANEL} pointer-events-auto absolute left-1/2 top-[112px] z-10 -translate-x-1/2 rounded-full px-4 py-2 text-sm font-semibold`} data-testid="lights-pending">Turning your lights on…</div>
      )}
      {econ && <GoalTracker econ={econ} stop={stopOf(econ, me)} collected={collected} hidden={Boolean(pickedNow) || (phone && Boolean(drawer || chat))} onOpen={show} />}
      {econ && me && guide && (
        <Guide econ={econ} me={me} clockOffset={eco.clockOffset} hidden={!calm || Boolean(pickedNow) || (phone && Boolean(drawer || chat))}
          className={drawer || chat ? "sm:right-[412px]" : ""} onOpen={show} onCollect={collect} onInvite={invite} onShare={() => shareDock(me)}
          onOff={() => {
            setGuide(false);
            card({ icon: Compass, tone: "grey", title: "Guide off. Turn it back on from the menu.", actions: [["Undo", () => setGuide(true)]] }, { sound: false, duration: 5000 });
          }} />
      )}
      {econ && (
        <ActionDock econ={econ} clockOffset={eco.clockOffset} meK={auth.user?.k} className={`${pickedNow ? "max-sm:hidden" : ""} ${drawer || chat ? "sm:right-[412px]" : ""}`}
          onCollect={collect} onOpen={show} onTrade={trade} onInvite={invite} />
      )}
      {auth.ready && !auth.user && <SignInCta at={at} error={signinError} onSignIn={signIn} />}

      {touring && <Tour me={me} user={auth.user} econ={econ} stops={stops} onFocus={setFocus} onDone={endTour} />}
      {lit && picking && (
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20">
          <PeoplePicker me={me} onSaved={refresh} onTagged={() => markDone("tag")} onClose={() => setPicking(false)} />
        </div>
      )}
      {!drawer && !chat && (
        <div className="absolute right-4 bottom-12 hidden sm:block">
          <Minimap city={city} me={city.mine} onPick={setFocus} />
        </div>
      )}
      {calm && <Chat user={auth.user} me={me} open={chat} onOpenChange={setChat} onUnread={setUnread} onSignIn={signIn} onVisit={fly} />}
      <Toaster position="top-center" offset={124} mobileOffset={124} toastOptions={{ unstyled: true }}
        style={{ left: drawer || chat ? "calc(50% - 206px)" : undefined, transition: "left 0.3s" }} />
      <Drawer state={drawer} onClose={closeDrawer} onTab={(tab) => setDrawer({ tab, t: Date.now() })} town={town} city={city} me={me} user={auth.user} eco={eco}
        onCollect={collect} onTrade={trade} onFly={fly} onLot={([x, y]) => setFocus([tileOf(x), tileOf(y)])} onShare={() => shareDock(me)}
        onFireworks={() => { loadTown(); flyHome(); }} onSignIn={signIn} />

      {pickedNow && (
        <div className="pointer-events-none absolute bottom-12 left-2 z-20 sm:bottom-auto sm:left-4 sm:top-[80px]">
          <Inspector house={pickedNow} nb={nbById[pickedNow.owner.nb]} user={auth.user} econ={econ} mayorK={mayorK}
            onTrade={trade} onChallenge={(p) => { setPicked(null); show("economy", "duels", { challenge: { k: p.k, h: p.h, a: p.a } }); }}
            onOpen={show} onFly={fly} onChanged={refresh} onLeft={() => { window.location.href = "/"; }} onClose={() => setPicked(null)} />
        </div>
      )}
      {h?.owner && !picked && (
        <div className="absolute pointer-events-none z-10" style={{ left: hover.x + 12, top: hover.y + 12 }}>
          <div className={`${PANEL} whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold`} data-testid="hover-pill">
            @{h.owner.h}{h.mansion && <Gem className="ml-1 inline h-3 w-3 text-x-rose" />}
            {h.owner.s === "f" ? <span className="money ml-1.5 tabular-nums">{fmt(h.owner.e)}</span> : <span className="ml-1.5 text-x-mute">dark</span>}
          </div>
        </div>
      )}
      {paper && <Newspaper onVisit={fly} onClose={closePaper} />}
      <TickerBar events={town.ticker} people={Object.fromEntries(town.residents.map((r) => [r.k, r]))} live={live} onVisit={fly} />
      <RevealLayer />
      <CoinLayer />
      <Splash done={built} />
    </div>
  );
}
